import React from 'react';
import { motion } from 'framer-motion';

const Process = () => {
  const steps = [
    {
      number: '01',
      title: 'Discover',
      subtitle: 'Understanding & Research',
      description: 'I dive deep into your vision, understanding your users, market, and goals. Through research and discovery sessions, I uncover the core problems we need to solve.',
      icon: 'search',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      number: '02',
      title: 'Build',
      subtitle: 'Design & Development',
      description: 'With a clear roadmap, I craft beautiful, functional solutions using cutting-edge technology. Every line of code is written with purpose and performance in mind.',
      icon: 'build',
      color: 'from-emerald-400 to-emerald-500',
    },
    {
      number: '03',
      title: 'Launch',
      subtitle: 'Deploy & Optimize',
      description: 'I make sure your product launches seamlessly and keeps improving over time, with ongoing support, optimization, and innovation that keeps you ahead of the curve.',
      icon: 'launch',
      color: 'from-sky-400 to-sky-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen py-20 px-4 relative overflow-hidden">
      
      {/* Gradient divider */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-500" />
      
      {/* Floating background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-clash font-bold mb-6 process-heading">
            My <span className="gradient-text">Process</span>
          </h2>
          <p className="text-xl text-gray-400 dark:text-gray-400 text-gray-600 font-inter max-w-3xl mx-auto">
            A systematic approach to creating digital experiences that not only meet expectations but exceed them.
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-20"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={itemVariants}
              className={`flex flex-col lg:flex-row items-center gap-12 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Step Number & Icon */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {/* Background circle */}
                  <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center relative`}>
                    {/* Glow effect */}
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${step.color} blur-xl opacity-50`} />
                    
                    {/* Step number */}
                    <div className="relative z-10 text-center">
                      <div className="text-4xl font-clash font-bold text-white mb-2">
                        {step.number}
                      </div>
                      <div className="w-8 h-8 mx-auto">
                        {step.icon === 'search' && (
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                          </svg>
                        )}
                        {step.icon === 'build' && (
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                          </svg>
                        )}
                        {step.icon === 'launch' && (
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Connecting line (except for last step) */}
                  {index < steps.length - 1 && (
                    <motion.div
                      className="hidden lg:block absolute top-32 left-1/2 transform -translate-x-1/2 w-1 h-20 bg-gradient-to-b from-gray-600 to-transparent"
                      initial={{ height: 0 }}
                      whileInView={{ height: 80 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.5 }}
                    />
                  )}
                </div>
              </div>

              {/* Step Content */}
              <div className="flex-1 max-w-2xl">
                <motion.div
                  initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <h3 className="text-4xl md:text-5xl font-clash font-bold process-heading mb-2">
                      {step.title}
                    </h3>
                    <p className="text-xl text-gray-400 dark:text-gray-400 text-gray-600 font-inter">
                      {step.subtitle}
                    </p>
                  </div>
                  
                  <p className="text-lg text-gray-300 dark:text-gray-300 text-gray-700 font-inter leading-relaxed">
                    {step.description}
                  </p>
                  
                  {/* Progress indicator */}
                  <div className="pt-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${step.color}`} />
                      <div className="text-sm text-gray-500 dark:text-gray-500 text-gray-600 font-inter">
                        Step {index + 1} of {steps.length}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-20"
        >
          <div className="glass rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-clash font-bold process-heading mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-gray-400 dark:text-gray-400 text-gray-600 text-lg mb-8">
              Let's work together to bring your vision to life with a process that's proven to deliver exceptional results.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary text-lg px-8 py-4"
              data-cursor="pointer"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Let's Build Something Amazing
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Process;
